import { getIpAddress } from "@/lib/utils";
import { USER_API } from "./index";
import { baseApi } from "@/lib/axios";
import { InfoOwner, User, UserRegisterDTO } from "@/model/user";

export async function registerNewUser(data: {
  userId: string;
  otpTokenPhone: string;
  otpTokenEmail: string;
  email: string;
  phoneNumber: string;
  password: string;
  isRetxWallet: boolean;
  fingerprint: string;
}) {
  const ipAddress = await getIpAddress();
  return baseApi<User>({
    url: USER_API.urlRegister,
    method: "POST",
    headers: {
      "otp-token-phone": data.otpTokenPhone,
      "otp-token-email": data.otpTokenEmail,
      "x-device-real-ip": ipAddress,
    },
    data: {
      userId: data.userId,
      email: data.email,
      phoneNumber: data.phoneNumber,
      password: data.password,
      isRetxWallet: data?.isRetxWallet,
      fingerprint: data.fingerprint,
    },
  });
}

export function checkEmailOrPhoneNumberNotExisted(
  data: { phoneNumber?: string; email?: string } = {
    phoneNumber: "",
    email: "",
  }
) {
  return baseApi<boolean>({
    url: USER_API.urlCheckExistedUser,
    method: "POST",
    data,
  });
}

export function checkEmailOrTaxCodeNotExisted(
  data: { taxCode?: string; email?: string } = { taxCode: "", email: "" }
) {
  return baseApi<boolean>({
    url: USER_API.urlCheckExistedBusiness,
    method: "POST",
    data,
  });
}

export function validateUserRegister(data: UserRegisterDTO) {
  return baseApi<{ userId: string }>({
    url: USER_API.urlValidateUser,
    method: "POST",
    data,
  });
}

export function userUpdateWallet(data: {
  walletAddress: string;
  chainId: string;
  soulBoundId: number;
  privateKeyHash?: string;
  mnemonicHash?: string;
}) {
  return baseApi<{
    data: {
      user: User;
    };
  }>({
    url: USER_API.urlUpdateWallet,
    method: "PUT",
    data,
  });
}

export function userForgotPassword(data: {
  identity: string;
  newPassword: string;
  otpToken: string;
  details: object;
  privateKeyHash?: string;
  mnemonicHash?: string;
}) {
  return baseApi<{ data: { dataEncode: string; email: string } }>({
    url: USER_API.urlForgotPassword,
    method: "POST",
    headers: {
      [`otp-token-${data.identity == "email" ? "email" : "phone"}`]:
        data.otpToken,
    },
    data: {
      newPassword: data.newPassword,
      details: data.details,
      privateKeyHash: data.privateKeyHash,
      mnemonicHash: data.mnemonicHash,
    },
  });
}

export function updateAvatar({ avatarUrl }: { avatarUrl: string }) {
  return baseApi<{ message: string }>({
    url: USER_API.urlUpdateAvatar,
    method: "PUT",
    data: {
      avatarUrl,
    },
  });
}

export function verifyPassword(data: { password: string } = { password: "" }) {
  return baseApi<string>({
    url: USER_API.urlVerifyPassword,
    method: "POST",
    data,
  });
}

export function addPassword(data: { password: string }) {
  return baseApi<{ message: string }>({
    url: USER_API.urlAddPassword,
    method: "PUT",
    data,
  });
}

export function checkPasswordExisted() {
  return baseApi<boolean>({
    url: USER_API.urlCheckPasswordExisted,
    method: "GET",
  });
}

export function checkKeyHashExisted() {
  return baseApi<{
    privateKeyHash: boolean;
    mnemonicHash: boolean;
  }>({
    url: USER_API.urlCheckKeyHash,
    method: "GET",
  });
}

export function updatePassword(data: {
  oldPassword: string;
  newPassword: string;
}) {
  return baseApi<{ message: string }>({
    url: USER_API.urlUpdatePassword,
    method: "PUT",
    data,
  });
}

export function updatePhoneNumber(data: {
  phoneNumber: string;
  confirmSmsOtpToken: string;
  otpToken: string;
}) {
  return baseApi({
    url: USER_API.urlUpdatePhoneNumber,
    method: "PUT",
    headers: {
      "otp-token-phone": data.otpToken,
    },
    data: {
      phoneNumber: data.phoneNumber,
      confirmSmsOtpToken: data.confirmSmsOtpToken,
    },
  });
}

export function addNewPhoneNumber(data: {
  phoneNumber: string;
  otpToken: string;
  userId: string;
}) {
  return baseApi({
    url: USER_API.urlAddNewPhone,
    method: "PUT",
    headers: {
      "otp-token-phone": data.otpToken,
    },
    data: {
      phoneNumber: data.phoneNumber,
    },
  });
}

export function updateGlobalPhoneNumber(data: { phoneNumber: string }) {
  return baseApi({
    url: USER_API.urlUpdateGlobalPhoneNumber,
    method: "PUT",
    data: { phoneNumber: data.phoneNumber },
  });
}

export function addNewGlobalPhoneNumber(data: {
  phoneNumber: string;
  userId: string;
}) {
  return baseApi({
    url: USER_API.urlAddNewGlobalPhone,
    method: "PUT",
    data: { phoneNumber: data.phoneNumber },
  });
}

export function addNewEmail(data: {
  email: string;
  otpToken: string;
  userId: string;
}) {
  return baseApi({
    url: USER_API.urlAddNewEmail,
    method: "PUT",
    headers: {
      "otp-token-email": data.otpToken,
    },
    data: {
      email: data.email,
    },
  });
}

export function updateEmail(data: {
  email: string;
  confirmEmailOtpToken: string;
  otpToken: string;
}) {
  return baseApi({
    url: USER_API.urlUpdateEmail,
    method: "PUT",
    headers: {
      "otp-token-email": data.otpToken,
    },
    data: {
      confirmEmailOtpToken: data.confirmEmailOtpToken,
      email: data.email,
    },
  });
}

export function isExistUsername(username: string) {
  return baseApi<boolean>({
    url: USER_API.urlIsExistUsername(username),
    method: "GET",
  });
}

export function getInfoUserByWallet(walletAddress: string) {
  return baseApi<InfoOwner>({
    url: USER_API.urlGetInfoUserByWallet(walletAddress),
    method: "GET",
  });
}

export function getSystemUserByWallet(walletAddress: string) {
  return baseApi<InfoOwner>({
    url: USER_API.getSystemUserByWallet(walletAddress),
    method: "GET",
  });
}

export function updateUserAddress(id: string, currentAddress: string) {
  return baseApi<{ userId: string }>({
    url: USER_API.urlUpdateAddress,
    method: "PUT",
    data: {
      currentAddress,
    },
  });
}

export function getPrivateHash() {
  return baseApi<string>({
    url: USER_API.urlPrivateKey,
    method: "GET",
  });
}

export function getMnemonicHash() {
  return baseApi<string>({
    url: USER_API.urlMnemonicHash,
    method: "GET",
  });
}

export function getUserByKeyword(params: { keyword: string }) {
  return baseApi<User>({
    url: USER_API.urlGetUserByKeyword,
    method: "GET",
    params,
  });
}

export function updateWebsiteProfile(data: { website: string }) {
  return baseApi<User>({
    url: USER_API.urlUpdateWebsiteProfile,
    method: "PUT",
    data,
  });
}

export function getNonce() {
  return baseApi<{ nonce: string; nonceId: string }>({
    url: USER_API.urlGetNonce,
    method: "GET",
  });
}

export function verifyMessage(data: {
  fingerprint: string;
  message: string;
  signature: string;
  nonceId: string;
}) {
  return baseApi<User>({
    url: USER_API.urlVerifyMessage,
    method: "POST",
    data,
  });
}

export function checkWalletAddressExisted(walletAddress: string) {
  return baseApi<boolean>({
    url: USER_API.urlCheckWalletExisted(walletAddress),
    method: "GET",
  });
}
